import logo from 'assets/header-prevencor-xx.png';
import md5 from 'md5';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import fetchImageBase64 from 'utils/helpers';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

function sectionTitle(text) {
  return {
    text,
    bold: true,
    decoration: 'underline',
    margin: [0, 6, 0, 2],
  };
}

function textLine(label, value) {
  if (value === null || value === undefined || value === '') return null;
  return {
    text: [{ text: `${label}: `, bold: true }, String(value)],
    margin: [0, 1, 0, 1],
  };
}

function listSection(title, items, keys = ['DESCRICAO', 'conclusoes', 'CONTRASEG']) {
  if (!Array.isArray(items) || !items.length) return [];

  const lines = items
    .map((item) => {
      const first = keys.map((k) => String(item?.[k] ?? '').trim()).find((v) => v.length > 0);
      return first || '';
    })
    .filter(Boolean);

  if (!lines.length) return [];

  return [sectionTitle(title), ...lines.map((line) => ({ text: line, margin: [10, 0, 0, 1] }))];
}

function listItems(items, keys = ['DESCRICAO', 'conclusoes', 'CONTRASEG']) {
  if (!Array.isArray(items) || !items.length) return [];

  const lines = items
    .map((item) => {
      const first = keys.map((k) => String(item?.[k] ?? '').trim()).find((v) => v.length > 0);
      return first || '';
    })
    .filter(Boolean);

  return lines.map((line) => ({ text: line, margin: [10, 0, 0, 1] }));
}

function dotMetricLine(label, value, suffix = '') {
  if (value === null || value === undefined || value === '') return null;

  const labelText = String(label || '');
  const dots = '.'.repeat(Math.max(6, 38 - labelText.length));
  const suffixText = suffix ? ` ${suffix}` : '';

  return {
    text: `${labelText}${dots}: ${value}${suffixText}`,
    margin: [0, 0, 0, 1],
  };
}

function format1(value) {
  if (value === null || value === undefined || value === '') return '';
  return Number(value).toFixed(1);
}

export async function generatePDFEco({ eco }) {
  if (!eco) return;

  const logoBase64 = await fetchImageBase64(logo);
  const hashAssinatura = md5(JSON.stringify(eco));

  const content = [
    {
      text: 'LAUDO DE ECOCARDIOGRAMA',
      bold: true,
      decoration: 'underline',
      alignment: 'center',
      margin: [0, 0, 0, 4],
    },
    {
      text: 'Eco Uni e Bi com Doppler Color',
      bold: true,
      decoration: 'underline',
      alignment: 'center',
      margin: [0, 0, 0, 10],
    },
    {
      text: [{ text: 'Paciente: ', bold: true }, `${eco.CODPAC || '-'} - ${eco.NOMEPAC || '-'}`],
      margin: [0, 0, 0, 1],
    },
    {
      columns: [
        {
          text: [
            { text: 'Sexo: ', bold: true },
            String(eco.Sexo || '-'),
            { text: '   Idade: ', bold: true },
            `${eco.idadePAC ?? '-'} anos`,
          ],
          width: '*',
        },
        {
          text: [
            { text: 'Data do Exame: ', bold: true },
            `${String(eco.dataEcoFmt || '-')} - Exame: ${String(eco.CODECO || '')}`,
          ],
          width: 'auto',
          alignment: 'right',
        },
      ],
      margin: [0, 1, 0, 1],
    },
    {
      text: [{ text: 'Médico que Realizou: ', bold: true }, `Dr.(a): ${eco.Realizou || '-'}`],
      margin: [0, 1, 0, 1],
    },
    {
      text: [{ text: 'Médico que Solicitou: ', bold: true }, `Dr.(a): ${eco.Solicitou || '-'}`],
      margin: [0, 1, 0, 1],
    },
    {
      text: [{ text: 'Convênio: ', bold: true }, String(eco.SIGLACONV || '-')],
      margin: [0, 1, 0, 6],
    },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }],
      margin: [0, 0, 0, 6],
    },

    ...listSection('Observações Gerais', eco.obs),

    sectionTitle('Diâmetros e Função Sistólica do Ventrículo Esquerdo'),
    {
      columns: [
        {
          width: '*',
          stack: [
            dotMetricLine('Septo', format1(eco.SEPTO), '(cm)'),
            dotMetricLine('PP VE', format1(eco.PPVE), '(cm)'),
            dotMetricLine(
              'Relação Septo/PP VE',
              eco.SEPTO != null && eco.PPVE != null && Number(eco.PPVE) !== 0
                ? (Number(eco.SEPTO) / Number(eco.PPVE)).toFixed(2)
                : ''
            ),
            dotMetricLine('Diâmetro Diastólico', format1(eco.DD), '(cm)'),
            dotMetricLine('Diâmetro Sistólico', format1(eco.DS), '(cm)'),
            dotMetricLine(
              'Fração de Encurtamento (%)',
              eco.DD != null && eco.DS != null && Number(eco.DD) !== 0
                ? (((Number(eco.DD) - Number(eco.DS)) / Number(eco.DD)) * 100).toFixed(1)
                : ''
            ),
          ].filter(Boolean),
        },
        {
          width: '*',
          stack: [
            dotMetricLine('Fração de Ejeção(Monoplanar)', eco.fracaoEjecaoMono ?? ''),
            dotMetricLine('Massa do VE (g/m2)', eco.massaVE ?? ''),
            dotMetricLine(
              'Cálculo da FE (Método de Simpson)-(%)',
              eco.FRAEJECBIPLA != null || eco.FE != null
                ? Number(eco.FRAEJECBIPLA ?? eco.FE).toFixed(0)
                : ''
            ),
          ].filter(Boolean),
        },
      ],
      columnGap: 20,
      margin: [0, 0, 0, 4],
    },

    ...listSection('Achados Descritivos do VE', eco.achados),
    ...listSection('Contração Segmentar', eco.contraRegiao, ['CONTRASEG']),

    sectionTitle('Avaliação da Função Diastólica do Ventrículo Esquerdo'),
    textLine(
      'Índice de Performance',
      eco.indicePM != null && !Number.isNaN(Number(eco.indicePM))
        ? `${Number(eco.indicePM).toFixed(2)} (Normal < 0.42)`
        : ''
    ),
    textLine(
      'Estimativa do Débito Cardíaco',
      eco.estimativaDC != null && !Number.isNaN(Number(eco.estimativaDC))
        ? `${eco.estimativaDC} l/min/m2 (2.4 a 4.2)`
        : ''
    ),
    ...listItems(eco.avDiast),

    sectionTitle('Átrio Esquerdo'),
    textLine(
      'Diâmetro do Átrio Esquerdo',
      eco.DAE != null ? `${Number(eco.DAE).toFixed(1)} cm` : ''
    ),
    textLine(
      'IVAE (Volume AE)',
      eco.VOL_AE != null || eco.VTIVSVE4 != null
        ? `${Number(eco.VOL_AE ?? eco.VTIVSVE4).toFixed(0)} (Normal até 34 ml/m2)`
        : ''
    ),
    ...listItems(eco.atrioEsq),

    ...listSection('Átrio Direito', eco.atrioDir),
    ...listSection('Veia Cava Inferior', eco.veiaCavaInf),
    ...(eco.VA != null || (Array.isArray(eco.ventriDir) && eco.ventriDir.length > 0)
      ? [
          sectionTitle('Ventrículo Direito'),
          textLine(
            'Diâmetro Ventrículo Direito',
            eco.VA != null ? `${Number(eco.VA).toFixed(1)} cm` : ''
          ),
          ...listItems(eco.ventriDir),
        ]
      : []),

    ...(eco.VTIVSVE1 != null || (Array.isArray(eco.aorta) && eco.aorta.length > 0)
      ? [
          sectionTitle('Aorta'),
          textLine(
            'Diâmetro Aorta',
            eco.VTIVSVE1 != null ? `${Number(eco.VTIVSVE1).toFixed(1)} cm` : ''
          ),
          ...listItems(eco.aorta),
        ]
      : []),

    sectionTitle('Valva Mitral'),
    ...listItems(eco.valvaMitral),
    ...listItems(eco.refVM),

    sectionTitle('Valva Aórtica'),
    textLine(
      'Diâmetro da Raiz Aórtica',
      eco.DAVAO != null ? `${Number(eco.DAVAO).toFixed(1)} cm` : ''
    ),
    ...listItems(eco.valvaAO),
    ...listItems(eco.refVAO),
    ...listSection('Valva Tricúspide', eco.valvaTricu),
    ...listItems(eco.refVT),

    sectionTitle('Valva Pulmonar'),
    textLine(
      'Diâmetro do Anel Pulmonar',
      eco.DAVP != null ? `${Number(eco.DAVP).toFixed(1)} cm` : ''
    ),
    ...listItems(eco.valvaPulmo),
    ...listItems(eco.refVP),

    ...listSection('Pericárdio', eco.pericardio),

    ...(eco.AVM != null || eco.ORIFICIO_VM != null || eco.GMVM != null || eco.VOLUME_VM != null
      ? [
          sectionTitle('Doppler Mitral'),
          textLine(
            'Área Valva Mitral',
            eco.AVM != null ? `${Number(eco.AVM).toFixed(1)} cm2 ${eco.valorAVM ?? ''}` : ''
          ),
          textLine(
            'Orifício Regurgitação Mitral (PISA)',
            eco.ORIFICIO_VM != null ? `${Number(eco.ORIFICIO_VM).toFixed(2)} cm2` : ''
          ),
          textLine('Gradiente Médio', eco.GMVM != null ? `${eco.GMVM} mmHg` : ''),
          textLine(
            'Volume Regurgitação Mitral',
            eco.VOLUME_VM != null ? `${eco.VOLUME_VM} cc` : ''
          ),
        ]
      : []),

    ...(eco.AREA_AO != null || eco.GMVAO != null || eco.GMVSVE != null
      ? [
          sectionTitle('Doppler Aórtico'),
          textLine(
            'Área Valva Aórtica',
            eco.AREA_AO != null
              ? `${Number(eco.AREA_AO).toFixed(2)} cm2 ${eco.valorAVAO ?? ''}`
              : ''
          ),
          textLine(
            'Gradiente Médio da Valva Aórtica',
            eco.GMVAO != null ? `${eco.GMVAO} mmHg` : ''
          ),
          textLine(
            'Gradiente Médio Via de Saída do VE',
            eco.GMVSVE != null ? `${eco.GMVSVE} mmHg` : ''
          ),
        ]
      : []),

    ...(eco.GMVT != null || eco.estimativaPSVD != null
      ? [
          sectionTitle('Doppler Tricúspide'),
          textLine(
            'Gradiente Médio da Valva Tricúspide',
            eco.GMVT != null ? `${eco.GMVT} mmHg` : ''
          ),
          textLine(
            'Estimativa da Pressão Sistólica da AP',
            eco.estimativaPSVD != null ? `${eco.estimativaPSVD} mmHg` : ''
          ),
        ]
      : []),

    ...(eco.GMVP != null
      ? [
          sectionTitle('Doppler Pulmonar'),
          textLine('Gradiente Médio da Valva Pulmonar', eco.GMVP != null ? `${eco.GMVP} mmHg` : ''),
        ]
      : []),

    ...(eco.GMPM != null || (Array.isArray(eco.refluxoPM) && eco.refluxoPM.length > 0)
      ? [
          sectionTitle('Prótese Mitral'),
          textLine('Gradiente Médio da Prótese Mitral', eco.GMPM != null ? `${eco.GMPM} mmHg` : ''),
          ...listSection('Refluxo Prótese Mitral', eco.refluxoPM, ['refluxoPM']),
        ]
      : []),

    ...(eco.GMPAO != null || (Array.isArray(eco.refluxoPAO) && eco.refluxoPAO.length > 0)
      ? [
          sectionTitle('Prótese Aórtica'),
          textLine(
            'Gradiente Médio da Prótese Aórtica',
            eco.GMPAO != null ? `${eco.GMPAO} mmHg` : ''
          ),
          ...listSection('Refluxo Prótese Aórtica', eco.refluxoPAO, ['refluxoPAO']),
        ]
      : []),

    ...(eco.GMPT != null || (Array.isArray(eco.refluxoPT) && eco.refluxoPT.length > 0)
      ? [
          sectionTitle('Prótese Tricúspide'),
          textLine(
            'Gradiente Médio da Prótese Tricúspide',
            eco.GMPT != null ? `${eco.GMPT} mmHg` : ''
          ),
          ...listSection('Refluxo Prótese Tricúspide', eco.refluxoPT, ['refluxoPT']),
        ]
      : []),

    ...(eco.GMPP != null || (Array.isArray(eco.refluxoPP) && eco.refluxoPP.length > 0)
      ? [
          sectionTitle('Prótese Pulmonar'),
          textLine(
            'Gradiente Médio da Prótese Pulmonar',
            eco.GMPP != null ? `${eco.GMPP} mmHg` : ''
          ),
          ...listSection('Refluxo Prótese Pulmonar', eco.refluxoPP, ['refluxoPP']),
        ]
      : []),

    ...(String(eco.OBSERVACOES || '').trim()
      ? [
          sectionTitle('Observações Finais'),
          { text: String(eco.OBSERVACOES), margin: [10, 0, 0, 1] },
        ]
      : []),

    sectionTitle('Conclusões'),
    ...listItems(eco.valvulopatia, ['valvulopatia']),
    ...listItems(eco.protese, ['protese']),
    ...listItems(eco.contraSeg, ['contraSeg']),
    ...listItems(eco.conclusoes, ['conclusoes']),

    {
      text: 'Conferido e assinado eletronicamente por:',
      italics: true,
      alignment: 'center',
      margin: [0, 20, 0, 3],
    },
    {
      text: `Dr(a).: ${eco.Realizou || '-'}`,
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 3],
    },
    {
      text: `Assinatura Digital: ${hashAssinatura}`,
      bold: true,
      italics: true,
      alignment: 'center',
      fontSize: 9,
      margin: [0, 0, 0, 12],
    },
  ].filter(Boolean);

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 90, 40, 68],
    header: function () {
      return {
        stack: [
          {
            image: logoBase64,
            width: 460,
            alignment: 'center',
            margin: [0, 6, 0, 2],
          },
          {
            text: String(eco.CODECO || ''),
            bold: true,
            alignment: 'right',
            fontSize: 10,
            margin: [40, 0, 40, 0],
          },
        ],
      };
    },
    footer: function (currentPage, pageCount) {
      return [
        {
          canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }],
          margin: [40, 2, 40, 0],
        },
        {
          text: 'Rua Cláudio Manuel da Costa, 56 - Fone (14) 3402-2700/Fax: (14) 3402-2705 - Jd Maria Isabel - CEP 17.515-460 - Marília/SP',
          alignment: 'center',
          fontSize: 7,
          margin: [40, 2, 40, 0],
        },
        {
          text: 'prevencor@unimedmarilia.com.br',
          alignment: 'center',
          fontSize: 7,
          margin: [40, 0, 40, 0],
        },
        {
          text: `Pág: ${currentPage} de ${pageCount}`,
          alignment: 'center',
          fontSize: 8,
          bold: true,
          margin: [40, 1, 40, 0],
        },
      ];
    },
    content,
    defaultStyle: { fontSize: 10 },
  };

  pdfMake.createPdf(docDefinition).open();
}
